import type { IndexedSentence, IndexedWordToken } from '../show-structure/utils/script-text-segmentation.ts';
import type { ActiveLineNoteSelection, TextRange } from './types/line-notes.ts';

export type { ActiveLineNoteSelection, TextRange } from './types/line-notes';

function clamp(value: number, maximum: number): number {
  return Math.max(0, Math.min(value, maximum));
}

export function normalizeTextRanges(ranges: TextRange[], lineTextSnapshot: string): TextRange[] {
  const snapshotLength = lineTextSnapshot.length;
  const clampedRanges = ranges
    .filter(({ start, end }) => Number.isInteger(start) && Number.isInteger(end))
    .map(({ start, end }) => ({
      start: clamp(start, snapshotLength),
      end: clamp(end, snapshotLength),
    }))
    .filter(({ start, end }) => start < end)
    .toSorted((left, right) => left.start - right.start || left.end - right.end);

  return clampedRanges.reduce<TextRange[]>((normalized, range) => {
    const previousRange = normalized.at(-1);
    if (!previousRange || previousRange.end <= range.start) {
      normalized.push(range);
    }
    return normalized;
  }, []);
}

export function createLineNoteSentenceSelection(
  blockId: string,
  sourceText: string,
  sentence: IndexedSentence
): ActiveLineNoteSelection {
  const sentenceText = sourceText.slice(sentence.start, sentence.end);
  const lineTextSnapshot = sentenceText.trim();
  const leadingWhitespaceLength = sentenceText.length - sentenceText.trimStart().length;

  return {
    blockId,
    lineTextSnapshot,
    sourceTextRange: {
      start: sentence.start + leadingWhitespaceLength,
      end: sentence.start + leadingWhitespaceLength + lineTextSnapshot.length,
    },
    highlightedWordRanges: [],
  };
}

export function toggleLineNoteWord(
  selection: ActiveLineNoteSelection,
  token: IndexedWordToken
): ActiveLineNoteSelection {
  const { sourceTextRange } = selection;
  if (!token.isWordLike || token.start < sourceTextRange.start || token.end > sourceTextRange.end) {
    return selection;
  }

  const wordRange = {
    start: token.start - sourceTextRange.start,
    end: token.end - sourceTextRange.start,
  };
  const existingRangeIndex = selection.highlightedWordRanges.findIndex(
    (range) => range.start === wordRange.start && range.end === wordRange.end
  );
  const highlightedWordRanges =
    existingRangeIndex === -1
      ? [...selection.highlightedWordRanges, wordRange]
      : selection.highlightedWordRanges.filter((_, index) => index !== existingRangeIndex);

  return {
    ...selection,
    highlightedWordRanges: normalizeTextRanges(highlightedWordRanges, selection.lineTextSnapshot),
  };
}

export function splitTextByRanges(
  lineTextSnapshot: string,
  highlightedWordRanges: TextRange[]
): Array<{ text: string; isHighlighted: boolean }> {
  const ranges = normalizeTextRanges(highlightedWordRanges, lineTextSnapshot);
  const parts: Array<{ text: string; isHighlighted: boolean }> = [];
  let cursor = 0;

  for (const range of ranges) {
    if (cursor < range.start) {
      parts.push({ text: lineTextSnapshot.slice(cursor, range.start), isHighlighted: false });
    }
    parts.push({ text: lineTextSnapshot.slice(range.start, range.end), isHighlighted: true });
    cursor = range.end;
  }

  if (cursor < lineTextSnapshot.length) {
    parts.push({ text: lineTextSnapshot.slice(cursor), isHighlighted: false });
  }

  return parts;
}
